import { LucideIcon } from 'lucide-react';

type MetricColor = 'blue' | 'green' | 'purple' | 'orange' | 'red';
type BadgeVariant = 'default' | 'success' | 'warning' | 'info';

interface MetricCardProps {
  title: string;
  value: string | number;
  icon: LucideIcon;
  badge?: { label: string; variant: BadgeVariant };
  subtitle?: string;
  secondaryValue?: string;
  trend?: { value: string; direction: 'up' | 'down' };
  color?: MetricColor;
  loading?: boolean;
}

const colorConfig: Record<MetricColor, { bg: string; icon: string; value: string }> = {
  blue: { bg: 'bg-blue-50', icon: 'text-blue-600', value: 'text-blue-600' },
  green: { bg: 'bg-green-50', icon: 'text-green-600', value: 'text-green-600' },
  purple: { bg: 'bg-purple-50', icon: 'text-purple-600', value: 'text-purple-600' },
  orange: { bg: 'bg-orange-50', icon: 'text-orange-600', value: 'text-orange-600' },
  red: { bg: 'bg-red-50', icon: 'text-red-600', value: 'text-red-600' },
};

const badgeConfig: Record<BadgeVariant, string> = {
  default: 'bg-gray-100 text-gray-700',
  success: 'bg-green-100 text-green-700',
  warning: 'bg-yellow-100 text-yellow-700',
  info: 'bg-blue-100 text-blue-700',
};

export function MetricCard({
  title,
  value,
  icon: Icon,
  badge,
  subtitle,
  secondaryValue,
  trend,
  color = 'blue',
  loading = false,
}: MetricCardProps) {
  const c = colorConfig[color];

  if (loading) {
    return (
      <div className="bg-white border border-border rounded-xl p-5">
        <div className="animate-pulse space-y-3">
          <div className="h-3 bg-gray-200 rounded w-3/4" />
          <div className="h-8 bg-gray-200 rounded w-1/2" />
          <div className="h-3 bg-gray-200 rounded w-1/3" />
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white border border-border rounded-xl p-5 hover:shadow-md transition-shadow">
      <div className="flex items-start justify-between mb-3">
        <div className={`${c.bg} p-2.5 rounded-lg`}>
          <Icon className={`w-5 h-5 ${c.icon}`} />
        </div>
        {badge && (
          <span className={`text-xs font-medium px-2 py-1 rounded-md ${badgeConfig[badge.variant]}`}>
            {badge.label}
          </span>
        )}
      </div>

      <div>
        <p className="text-sm text-muted-foreground mb-1">{title}</p>
        <div className="flex items-baseline gap-2">
          <p className={`text-3xl font-semibold ${c.value}`}>{value}</p>
          {trend && (
            <span className={`text-sm font-medium ${trend.direction === 'up' ? 'text-green-600' : 'text-red-600'}`}>
              {trend.direction === 'up' ? '↑' : '↓'} {trend.value}
            </span>
          )}
        </div>
        {secondaryValue && (
          <p className="text-xs text-muted-foreground mt-1">{secondaryValue}</p>
        )}
        {subtitle && (
          <p className="text-xs text-muted-foreground mt-1">{subtitle}</p>
        )}
      </div>
    </div>
  );
}
